import {
  ArrowDownward as ArrowDownwardIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import {
  projectWrapPricingCommit,
  recommendClasses,
  toExposureRows,
} from "components/pricing/glPricing/constructionRecommend";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as glExposuresStore from "store/glExposures";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const TRUNCATE_SELECT_SX = {
  "& .MuiSelect-select": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

// A construction input dropdown: the selected value truncates (no overflow) and a
// tooltip shows the full label on hover.
const SelectField = ({ label, value, options, onChange }) => (
  <Tooltip title={value ?? ""} placement={"top"}>
    <TextField
      select
      fullWidth
      variant={"filled"}
      label={label}
      InputLabelProps={{ shrink: true }}
      value={value ?? ""}
      onChange={onChange}
      sx={TRUNCATE_SELECT_SX}
    >
      <MenuItem value={""}>{"—"}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  </Tooltip>
);

// Collapsible Construction / Project-Wrap class recommender for the GL Exposures
// tab. Its own project type + insured interest + state + construction value are
// the recommender's working selections (construction* program fields): project
// type + state + value drive the class/payroll recommendation. "Copy to
// Exposures" appends the selected rows into the (unfilled) GL Exposures rows and
// commits project type + insured interest to the GL Pricing factors (without
// clobbering manual GL Pricing entries). Self-contained: reads the exposures
// store + gl_exposures config internally.
const ConstructionRecommendPanel = () => {
  const dispatch = useDispatch();
  const program = useSelector(programSelectors.getProgram);
  const exposures = useSelector(glExposuresStore.selectExposures);
  const { data: glConfig } = staticDataService.useConfigQuery("gl_exposures");
  const classCodeIndex = useMemo(
    () =>
      Object.fromEntries((glConfig?.classCodes ?? []).map((c) => [c.code, c])),
    [glConfig]
  );
  // Project-type + state options and the class mix come from the
  // construction_rating_config static-data config.
  const { data: cfg } = staticDataService.useConfigQuery(
    "construction_rating_config"
  );
  const projectTypes = cfg?.projectTypes ?? [];
  const insuredInterests = cfg?.insuredInterests ?? [];
  const states = cfg?.statePayrollCvRatio
    ? Object.keys(cfg.statePayrollCvRatio).sort()
    : [];
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (code) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const constructionProjectType = program?.constructionProjectType ?? null;
  const constructionInsuredInterest =
    program?.constructionInsuredInterest ?? null;
  const constructionState = program?.constructionState ?? null;
  const constructionValue = program?.constructionValue ?? null;

  const onUpdate = (values) => dispatch(programActions.updateProgram(values));
  const recommended = recommendClasses(
    constructionState,
    constructionProjectType,
    constructionValue,
    cfg ?? {}
  );

  // Copy to Exposures: append the recommended classes to the filled rows (drops
  // blanks so they land in empty slots), then commit project type + insured
  // interest to the GL Pricing factors (never overwriting a manual GL Pricing
  // entry — see projectWrapPricingCommit).
  const applyRecommendation = useCallback(
    (rows) => {
      if (!rows?.length) return;
      const filled = exposures.filter((r) => r.classCode || r.exposureAmount);
      dispatch(
        glExposuresStore.update({
          exposures: [...filled, ...toExposureRows(rows)],
        })
      );
      const commit = projectWrapPricingCommit(program);
      if (Object.keys(commit).length) {
        dispatch(programActions.updateProgram(commit));
      }
    },
    [exposures, program, dispatch]
  );

  return (
    <Stack spacing={0}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        onClick={() => setOpen((v) => !v)}
        sx={{
          cursor: "pointer",
          px: 2,
          py: 1.25,
          borderRadius: 1,
          backgroundColor: "grey.100",
        }}
      >
        <Typography
          fontSize={"0.875rem"}
          fontWeight={700}
          color={"text.secondary"}
        >
          {"Construction / Project-Wrap — Recommend Classes"}
        </Typography>
        {open ? (
          <ExpandLessIcon fontSize={"small"} />
        ) : (
          <ExpandMoreIcon fontSize={"small"} />
        )}
      </Stack>
      {open && (
        <Stack spacing={1.5} sx={{ pt: 1.5, pb: 1, px: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <SelectField
                label={"Project Type"}
                value={constructionProjectType}
                options={projectTypes}
                onChange={(e) =>
                  onUpdate({ constructionProjectType: e.target.value || null })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <SelectField
                label={"Insured Interest"}
                value={constructionInsuredInterest}
                options={insuredInterests}
                onChange={(e) =>
                  onUpdate({
                    constructionInsuredInterest: e.target.value || null,
                  })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <SelectField
                label={"State"}
                value={constructionState}
                options={states}
                onChange={(e) =>
                  onUpdate({ constructionState: e.target.value || null })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <PrettyNumberTextField
                label={"Construction Value"}
                InputLabelProps={{ shrink: true }}
                fullWidth
                onChangeNumber={(v) => onUpdate({ constructionValue: v })}
                value={constructionValue ?? null}
              />
            </Grid>
          </Grid>
          {recommended.length > 0 && (
            <Stack spacing={0.25}>
              <Typography variant={"overline"} color={"text.secondary"}>
                {"Recommended Classes — click to select (estimated payroll)"}
              </Typography>
              {recommended.map((r) => (
                <Stack
                  key={r.class_code}
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  onClick={() => toggle(r.class_code)}
                  sx={{ cursor: "pointer" }}
                >
                  <Checkbox
                    size={"small"}
                    checked={selected.has(r.class_code)}
                    sx={{ p: 0.25 }}
                  />
                  <Typography
                    fontSize={"0.8rem"}
                    sx={{ minWidth: 52, flexShrink: 0 }}
                  >
                    {r.class_code}
                  </Typography>
                  <Typography
                    noWrap
                    fontSize={"0.8rem"}
                    color={"text.secondary"}
                    title={classCodeIndex?.[r.class_code]?.label ?? ""}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    {classCodeIndex?.[r.class_code]?.label ?? ""}
                  </Typography>
                  <Typography
                    fontSize={"0.8rem"}
                    color={"text.secondary"}
                    sx={{ flexShrink: 0, minWidth: 72, textAlign: "right" }}
                  >
                    {`$${Math.round(r.exposure_amount).toLocaleString()}`}
                  </Typography>
                </Stack>
              ))}
              <Stack direction={"row"} sx={{ pt: 0.5 }}>
                <Button
                  variant={"contained"}
                  startIcon={<ArrowDownwardIcon />}
                  disabled={selected.size === 0}
                  onClick={() =>
                    applyRecommendation(
                      recommended.filter((r) => selected.has(r.class_code))
                    )
                  }
                >
                  {"Copy Selected to Exposures"}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ConstructionRecommendPanel;
